import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppCalloutBannerContent } from "@/components/banners/app-callout-banner/app-callout-banner-content";
import { isMemAgentSettingsMemAiUrl } from "@/components/banners/app-callout-banner/utils/app-banner-link-handling";
import { trackEvent } from "@/domains/metrics";
import { TrackedEvent } from "@/domains/metrics";
import { buildOriginalSubscriptionPlanMetricsPayload } from "@/domains/metrics/monetization";
import type { AppCalloutBannerEventParams } from "@/domains/metrics/tracked-event";
import { MEM_AGENT_REMINDERS_APP_CALLOUT_ID } from "@/store/app-callout/app-callout-ids";

interface AppCalloutBannerProps {}

export const AppCalloutBanner: FC<AppCalloutBannerProps> = observer(() => {
  const { store } = useAppStore();
  const [suppressedDismissControlCalloutId, setSuppressedDismissControlCalloutId] = useState<
    string | null
  >(null);
  const [isDismissSettingsUpdatePending, setIsDismissSettingsUpdatePending] = useState(false);
  const trackedViewSpaceAccountAppCalloutIdRef = useRef<string | null>(null);

  const storeWithOptionalMemAgentCallout = store as typeof store & {
    memAgentCallout?: typeof store.memAgentCallout;
  };
  const memAgentCalloutStore = storeWithOptionalMemAgentCallout.memAgentCallout;
  const memAgentCallout = memAgentCalloutStore?.visibleBanner ?? null;
  const appCallout = memAgentCallout
    ? null
    : store.spaceAccountAppCallouts.highestPriorityVisibleBanner;
  const banner = memAgentCallout ?? appCallout;
  const bannerDismissControlKey = memAgentCallout
    ? `client:${memAgentCallout.id}`
    : (appCallout?.id ?? null);
  const isDismissControlSuppressed =
    bannerDismissControlKey === suppressedDismissControlCalloutId &&
    banner?.primaryActionLabel !== null &&
    banner?.primaryActionUrl !== null;
  const isDismissible = Boolean(
    (memAgentCallout?.isDismissible ?? appCallout?.isDismissible) && !isDismissControlSuppressed
  );
  const trackingContext: AppCalloutBannerEventParams | null = useMemo(() => {
    if (!banner) {
      return null;
    }

    return {
      space_account_app_callout_id: memAgentCallout
        ? `client:${store.spaceAccounts.myPersonalSpaceAccountId}:${memAgentCallout.id}`
        : banner.id,
      app_callout_id: banner.appCalloutId,
      is_dismissible: isDismissible,
      ...buildOriginalSubscriptionPlanMetricsPayload({ store }),
    };
  }, [banner, isDismissible, memAgentCallout, store]);

  useEffect(() => {
    setIsDismissSettingsUpdatePending(false);

    if (
      suppressedDismissControlCalloutId !== null &&
      bannerDismissControlKey !== suppressedDismissControlCalloutId
    ) {
      setSuppressedDismissControlCalloutId(null);
    }
  }, [bannerDismissControlKey, suppressedDismissControlCalloutId]);

  useEffect(() => {
    if (!trackingContext) {
      trackedViewSpaceAccountAppCalloutIdRef.current = null;
      return;
    }

    if (
      trackedViewSpaceAccountAppCalloutIdRef.current ===
      trackingContext.space_account_app_callout_id
    ) {
      return;
    }

    trackedViewSpaceAccountAppCalloutIdRef.current = trackingContext.space_account_app_callout_id;
    trackEvent(TrackedEvent.AppCalloutBannerView, trackingContext);
    if (trackingContext.app_callout_id === MEM_AGENT_REMINDERS_APP_CALLOUT_ID) {
      trackEvent(TrackedEvent.RemindersCalloutVisible, trackingContext);
    }
  }, [trackingContext]);

  const handleDismiss = useCallback(async () => {
    if (!banner || !trackingContext) {
      return;
    }

    trackEvent(TrackedEvent.AppCalloutBannerDismissed, {
      ...trackingContext,
      url: banner.primaryActionUrl,
    });

    if (memAgentCallout && memAgentCalloutStore) {
      setIsDismissSettingsUpdatePending(true);
      try {
        await memAgentCalloutStore.dismiss();
      } finally {
        setIsDismissSettingsUpdatePending(false);
      }
      return;
    }

    if (appCallout) {
      await store.spaceAccountAppCallouts.dismissAppCallout({ appCallout });
    }
  }, [appCallout, banner, memAgentCallout, memAgentCalloutStore, store, trackingContext]);

  const hasProactiveAccess = store.subscriptions.featureAccess?.isProactive === true;

  const dismissAppCalloutAfterEnablingDismissal = useCallback(async () => {
    if (!appCallout) {
      return;
    }

    if (!appCallout.isDismissalEnabled) {
      await store.spaceAccountAppCallouts.enableAppCalloutDismissal({ appCallout });
    }

    await store.spaceAccountAppCallouts.dismissAppCallout({ appCallout });
  }, [appCallout, store.spaceAccountAppCallouts]);

  const handlePrimaryActionClick = useCallback(() => {
    if (memAgentCallout && memAgentCalloutStore) {
      if (hasProactiveAccess && isMemAgentSettingsMemAiUrl(memAgentCallout.primaryActionUrl)) {
        void memAgentCalloutStore.dismiss();
        return;
      }

      if (memAgentCallout.isDismissible) {
        return;
      }

      if (!memAgentCallout.primaryActionLabel || !memAgentCallout.primaryActionUrl) {
        return;
      }

      setSuppressedDismissControlCalloutId(`client:${memAgentCallout.id}`);
      void memAgentCalloutStore.enableDismissal();
      return;
    }

    if (!appCallout) {
      return;
    }

    if (hasProactiveAccess && isMemAgentSettingsMemAiUrl(appCallout.primaryActionUrl)) {
      void dismissAppCalloutAfterEnablingDismissal();
      return;
    }

    if (appCallout.isDismissible) {
      return;
    }

    if (appCallout.isDismissalEnabled) {
      return;
    }

    if (!appCallout.primaryActionLabel || !appCallout.primaryActionUrl) {
      return;
    }

    setSuppressedDismissControlCalloutId(appCallout.id);
    void store.spaceAccountAppCallouts.enableAppCalloutDismissal({ appCallout });
  }, [
    appCallout,
    dismissAppCalloutAfterEnablingDismissal,
    hasProactiveAccess,
    memAgentCallout,
    memAgentCalloutStore,
    store.spaceAccountAppCallouts,
  ]);

  const handlePrimaryActionComplete = useCallback(() => {
    setSuppressedDismissControlCalloutId(null);
  }, []);

  if (!banner || !trackingContext) {
    return null;
  }

  return (
    <AppCalloutBannerContent
      iconKind={banner.iconKind}
      iconPosition={banner.iconPosition}
      isDismissible={isDismissible}
      isDismissLoading={isDismissSettingsUpdatePending}
      primaryLabel={banner.primaryLabel}
      primaryLabelEmphasizedPrefix={
        "primaryLabelEmphasizedPrefix" in banner ? banner.primaryLabelEmphasizedPrefix : null
      }
      secondaryLabel={banner.secondaryLabel}
      inlineActionLabel={banner.inlineActionLabel}
      inlineActionUrl={banner.inlineActionUrl}
      primaryActionLabel={banner.primaryActionLabel}
      primaryActionUrl={banner.primaryActionUrl}
      primaryActionIconKind={banner.primaryActionIconKind}
      primaryActionButtonVariant={banner.primaryActionButtonVariant}
      secondaryActionLabel={banner.secondaryActionLabel}
      secondaryActionUrl={banner.secondaryActionUrl}
      secondaryActionIconKind={banner.secondaryActionIconKind}
      tertiaryActionLabel={banner.tertiaryActionLabel}
      tertiaryActionUrl={banner.tertiaryActionUrl}
      tertiaryActionIconKind={banner.tertiaryActionIconKind}
      trackingContext={trackingContext}
      handleDismiss={handleDismiss}
      handlePrimaryActionClick={handlePrimaryActionClick}
      handlePrimaryActionComplete={handlePrimaryActionComplete}
    />
  );
});
